import {
	benefitsAboveList,
	benefitsMagicEraser,
	howItWorkAboveList,
	howItWorkMagicEraser,
	whyUseAboveList,
	whyUseMagicEraser,
} from 'liftApp/data/magicEraser';
import React from 'react';
import {
	CTAScreen,
	CustomHero,
	ImageTextSection,
	Reviews,
} from '@/components/landing/reusableSections';
import { useTranslationContext } from '@/context/translation';
import {
	useLandingShownEvent,
	useSendToAmplitudeScrolledPercent,
	useTranslationWithLocale,
} from '@/hooks';
import { GenerateLandingLinkService, GoogleService } from '@/services';
import { reviewsLanding } from '@/data';
import {
	AMPLITUDE_EVENTS,
	GOOGLE_EVENTS,
	LANDING_AMPLITUDE_EVENTS,
	LOCAL_STORAGE_KEYS,
} from '@/constants';
import ctaScreen from '@/public/images/landing/ctaScreen.webp';
import benefit from '@/public/images/magic-eraser/benefits.webp';
import imgHero from '@/public/images/magic-eraser/heroImage.webp';
import howItWork from '@/public/images/magic-eraser/howItWorks.webp';
import whyUse from '@/public/images/magic-eraser/whyUse.webp';
import styles from './MagicEraserLanding.module.scss';

interface Props {}
const MagicEraserLanding: React.FC<Props> = () => {
	const { locale, translations } = useTranslationContext();
	const { t } = useTranslationWithLocale(
		'magic-eraser',
		locale,
		translations?.['magic-eraser'],
	);
	const { t: tReviews } = useTranslationWithLocale(
		'landing-reviews',
		locale,
		translations?.['landing-reviews'],
	);

	const handleMagicEraserClick = () => {
		GoogleService.logEvent(GOOGLE_EVENTS.LANDING_PASSED);
		window.location.href = GenerateLandingLinkService.funnelMagicEraser();
	};

	useSendToAmplitudeScrolledPercent(
		[50, 75, 100],
		LOCAL_STORAGE_KEYS.MAGIC_ERASER_SCROLLED_POINTS,
		AMPLITUDE_EVENTS.WEBAPP_LANDING_SCROLLED,
		{ type: LANDING_AMPLITUDE_EVENTS.MAGIC_ERASER_V3 },
	);

	useLandingShownEvent({ type: LANDING_AMPLITUDE_EVENTS.MAGIC_ERASER_V3 });
	return (
		<>
			<CustomHero
				image={imgHero}
				title={t('heroData.title')}
				subtitle={t('heroData.subtitle')}
				onClick={handleMagicEraserClick}
				buttonText={t('getStarted')}
			/>

			{/* HowItWork */}
			<ImageTextSection
				title={t('howItWorkData.mainTitle')}
				specialListItem
				textsAboveList={howItWorkAboveList}
				image={howItWork}
				data={howItWorkMagicEraser}
				buttonText={t('getStarted')}
				t={t}
				onClick={handleMagicEraserClick}
				classes={{
					itemInRowList: styles['magicEraserLanding__howItWork-itemInRowList'],
				}}
			/>

			<Reviews t={tReviews} data={reviewsLanding} />

			{/* Benefit */}
			<ImageTextSection
				title={t('benefitData.mainTitle')}
				textsAboveList={benefitsAboveList}
				image={benefit}
				data={benefitsMagicEraser}
				buttonText={t('getStarted')}
				t={t}
				onClick={handleMagicEraserClick}
				extraTextBelow={t('benefitData.extraText')}
				classes={{
					imageContainer: styles['magicEraserLanding__benefit-image-container'],
					list: styles['magicEraserLanding__benefit-list'],
				}}
			/>

			{/* WhyUse */}
			<ImageTextSection
				title={t('whyUseData.mainTitle')}
				textsAboveList={whyUseAboveList}
				image={whyUse}
				data={whyUseMagicEraser}
				buttonText={t('getStarted')}
				t={t}
				onClick={handleMagicEraserClick}
				extraTextBelow={t('whyUseData.extraText')}
			/>

			{/* TryLift */}
			<CTAScreen
				title1={t('tryLiftData.title1')}
				subtitle={t('tryLiftData.subtitle')}
				image={ctaScreen}
				altText={t('tryLiftData.altText')}
				buttonText={t('getStarted')}
				onClick={handleMagicEraserClick}
				classes={{
					titleInline: styles['magicEraserLanding__tryLift-title'],
				}}
			/>
		</>
	);
};

export default MagicEraserLanding;
